"use client";

import { PostSSAForm } from "@/actions/application/PostForm";
import { calculateSectionProgress } from "@/components/self_serve/SelfServeSection";
import axiosClient from "@/lib/admin/axiosClient";
import { EVENT_LIBRARY } from "@/lib/constants";
import handleServerAction from "@/lib/hooks/handleServerAction";
import useAnalytics from "@/lib/hooks/useAnalytics";
import { getApplicationFields } from "@/utils/shared";
import { Spinner, ToastId, useToast } from "@chakra-ui/react";
import { createContext, useEffect, useRef, useState } from "react";

const FormContext = createContext<{
  saveForm: (newSingleValue: any) => void;
  values: any;
  setValues: (values: any) => void;
  sections: any[];
  categories: any[];
  activeCategory: string;
  setActiveCategory: Function;
}>({
  saveForm: (newSingleValue: any) => {},
  values: {},
  setValues: () => {},
  sections: [],
  categories: [],
  activeCategory: "",
  setActiveCategory: (activeCategory: string) => {},
});

const ARRAY_FIELDS = [
  "medicalTests",
  "hospitals",
  "medicines",
  "employers",
  "jobListings",
  "priorMarriages",
  "helpingContacts",
  "otherNames",
  "childrenNames",
];

const FormProvider = ({
  children,
  defaultValues,
  isAdmin,
  profileId,
}: {
  children: React.ReactNode;
  defaultValues: any;
  isAdmin?: boolean;
  profileId?: number;
}) => {
  const track = useAnalytics();
  const toast = useToast();
  const toastIdRef = useRef<ToastId | null>(null);
  const [values, setValues] = useState(defaultValues);
  const [activeCategory, setActiveCategory] = useState("");
  const [sections, setSections] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);

  const savingRef = useRef<Promise<void> | null>(null);
  const latestValuesRef = useRef(values);

  const saveForm = async (newSingleValue: { [key: string]: any }) => {
    try {
      console.log("SaveForm", newSingleValue);

      latestValuesRef.current = {
        ...latestValuesRef.current,
        ...newSingleValue,
      };

      // If a save is already in progress, wait for it to finish
      while (savingRef.current) {
        await savingRef.current;
      }

      savingRef.current = (async () => {
        try {
          toastIdRef.current = toast({
            position: "bottom-right",
            duration: null,
            render: () => <Spinner color="brand.primary" />,
            containerStyle: {
              minWidth: "auto",
              padding: "24px",
            },
          });

          const payload = latestValuesRef.current; // Ensure we send the latest values

          ARRAY_FIELDS.forEach((field) => {
            if (payload[field] && !Array.isArray(payload[field])) {
              throw new Error(`${field} must be an array`);
            }
          });

          if (isAdmin) {
            const response = await axiosClient.post("/PostByClaimantId", {
              ClaimantMindsetUserId: profileId,
              Json: JSON.stringify(payload),
            });
          } else {
            const response = await handleServerAction(PostSSAForm, {
              Json: JSON.stringify(payload),
            });
          }

          setValues(payload);

          if (toastIdRef.current) {
            toast.close(toastIdRef.current);
          }

          if (!isAdmin) {
            track(EVENT_LIBRARY.SAVED_APPLICATION, {
              databaseKey: Object.keys(newSingleValue || {})[0],
            });
          }
        } catch (err) {
          console.error(err);
        } finally {
          savingRef.current = null; // Allow the next save to proceed
        }
      })();

      await savingRef.current;
    } catch (err) {
      console.error(err);
      toastIdRef.current = toast({
        title: "Error saving form",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    const updatedSections = getApplicationFields(values, activeCategory).filter(
      (section: any) => section.displayIf !== false
    );
    setSections(updatedSections);
    // group sections by section.category
    setCategories(
      updatedSections.reduce((acc: any, section: any) => {
        if (!acc[section.category]) {
          acc[section.category] = [];
        }
        const { progress } = calculateSectionProgress({
          section,
          values,
        });
        acc[section.category].push({
          ...section,
          progress,
        });
        return acc;
      }, [])
    );
  }, [activeCategory]);

  return (
    <FormContext.Provider
      value={{
        saveForm,
        values,
        setValues,
        sections,
        categories,
        activeCategory,
        setActiveCategory,
      }}
    >
      {children}
    </FormContext.Provider>
  );
};

export { FormContext, FormProvider };
